
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Form, Spinner } from 'react-bootstrap';
import { Pie, Bar } from 'react-chartjs-2';
import { OPCIONES_BASE, EJES_BASE, agruparEnOtros, datasetBarrasSimple } from '../../utils/graficos';
import TarjetaKpi from '../../components/ui/TarjetaKpi';
// Servicio de reportes
import { getAllReportes } from '../../services/reportes/reportesRTIService';
// Componente reutilizable para PDF
import { usePDFReportes } from '../../components/ReportesPDF';
import PageHeader from '../../components/ui/PageHeader';
import '../../style/pages/HomePage.css';
import useBreakpoint from '../../hooks/useBreakpoint';
import BottomSheet from '../../components/mobile/BottomSheet';
import BotonFiltros from '../../components/mobile/BotonFiltros';
import ChipsFiltros from '../../components/mobile/ChipsFiltros';



const BASE_OPTIONS = OPCIONES_BASE;
// Barras de una sola medida: sin leyenda (el título ya la nombra) y ejes del sistema.
const OPCIONES_BARRAS = { ...OPCIONES_BASE, plugins: { ...OPCIONES_BASE.plugins, legend: { display: false } }, scales: EJES_BASE };
const OPCIONES_BARRAS_H = { ...OPCIONES_BARRAS, indexAxis: 'y', scales: { x: EJES_BASE.y, y: EJES_BASE.x } };

const formatearFechaMientrasEscribe = (valor) => {
    const n = valor.replace(/\D/g,'');
    if (!n.length) return '';
    if (n.length<=2) return n;
    if (n.length<=4) return `${n.slice(0,2)}/${n.slice(2)}`;
    return `${n.slice(0,2)}/${n.slice(2,4)}/${n.slice(4,8)}`;
};

const convertirTextoAFecha = (texto) => {
    if (!texto?.trim()) return null;
    const partes = texto.replace(/[^\d/]/g,'').split('/');
    if (partes.length===3) {
        const [dia,mes,anio] = partes.map(Number);
        if (dia>0&&dia<=31&&mes>0&&mes<=12&&anio>=1900&&anio<=2100) {
            const f = new Date(anio,mes-1,dia);
            if (!isNaN(f.getTime())&&f.getDate()===dia&&f.getMonth()===mes-1) return f.toISOString().split('T')[0];
        }
    }
    return null;
};

function ReportesTramitesRTI() {
    const navigate = useNavigate();

    // Colores diferenciables en B&N para gráficos

    // ESTADOS SIMPLIFICADOS (Datos ya procesados por Backend)
    const [resumen, setResumen] = useState({
        totalTramites: 0,
        estadosActivos: 0,
        estadoMasFrecuenteNombre: '—',
        estadoMasFrecuenteCantidad: 0
    });
    const [porEstado, setPorEstado] = useState([]);
    const [tiempoPromedio, setTiempoPromedio] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filtros
    const [filtroFechaInicio, setFiltroFechaInicio] = useState('');
    const [filtroFechaFin, setFiltroFechaFin] = useState('');
    const [filtroFechaInicioTexto, setFiltroFechaInicioTexto] = useState('');
    const [filtroFechaFinTexto, setFiltroFechaFinTexto] = useState('');
    const [sheetFiltrosAbierto, setSheetFiltrosAbierto] = useState(false);
    const { esMobile } = useBreakpoint();

    const pieRef = useRef(null);
    const barRef = useRef(null);
    const barTiempoRef = useRef(null);

    // UN SOLO EFECTO: Recarga todo desde el Backend al filtrar
    useEffect(() => {
        const cargarReportes = async () => {
            try {
                setLoading(true);
                const filtros = {
                    fechaDesde: filtroFechaInicio || null,
                    fechaHasta: filtroFechaFin || null
                };

                console.log('📊 Cargando reportes con filtros:', filtros);
                const data = await getAllReportes(filtros);
                console.log('✅ Datos recibidos del backend:', data);

                if (data && data.resumen && data.porEstado && data.tiempoPromedio) {
                    setResumen(data.resumen);
                    setPorEstado(data.porEstado);
                    setTiempoPromedio(data.tiempoPromedio);
                    console.log('✓ Estados actualizados correctamente');
                } else {
                    console.warn('⚠️ Estructura de datos incompleta:', data);
                }
            } catch (err) {
                console.error('❌ Error cargando reportes RTI:', err);
                console.error('Detalles del error:', err.response?.data || err.message);
            } finally {
                setLoading(false);
            }
        };
        cargarReportes();
    }, [filtroFechaInicio, filtroFechaFin]);

    // CHART DATA: Construcción directa desde los estados del backend
    const datosPie = useMemo(() => {
        // Torta: paleta del sistema, máximo 6 porciones (el resto en "Otros").
        const pieAgrupado = agruparEnOtros(porEstado.map(e => e.nombreEstado), porEstado.map(e => e.cantidad));
        const chartData = {
            labels: pieAgrupado.labels,
            datasets: [{ data: pieAgrupado.data, backgroundColor: pieAgrupado.colores }]
        };
        console.log('📊 datosPie:', chartData);
        return chartData;
    }, [porEstado]);

    const datosBar = useMemo(() => {
        const chartData = {
            labels: porEstado.map(e => e.nombreEstado),
            datasets: [datasetBarrasSimple('Cantidad de trámites', porEstado.map(e => e.cantidad))]
        };
        console.log('📊 datosBar:', chartData);
        return chartData;
    }, [porEstado]);

    const datosBarTiempo = useMemo(() => {
        const chartData = {
            labels: tiempoPromedio.map(e => e.nombreEstado),
            datasets: [datasetBarrasSimple('Días promedio', tiempoPromedio.map(e => e.diasPromedio))]
        };
        console.log('📊 datosBarTiempo:', chartData);
        return chartData;
    }, [tiempoPromedio]);

    const limpiarFiltros = () => {
        setFiltroFechaInicio(''); setFiltroFechaFin('');
        setFiltroFechaInicioTexto(''); setFiltroFechaFinTexto('');
    };

    const { generarYDescargar } = usePDFReportes();

    const generarPDF = () => {
        generarYDescargar({
            tipo: 'RTI',
            resumen,
            porEstado,
            tiempoPromedio,
            referencias: {
                pieRef,
                barRef,
                barTiempoRef
            }
        });
    };

    if (loading) return (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '40vh' }}>
            <Spinner animation="border" style={{ color: '#10B981' }} />
            <span className="ms-3">Cargando reporte desde el servidor...</span>
        </div>
    );

    // Validación: verificar que hay datos para renderizar
    const hayDatos = porEstado && porEstado.length > 0;
    const hayDatosPromedios = tiempoPromedio && tiempoPromedio.length > 0;

    return (
        <div className="homepage-container area-rti">
            {/* Breadcrumb */}
            <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <span role="button" style={{ cursor: 'pointer', color: '#3B82F6', textDecoration: 'underline' }} onClick={() => navigate('/reportes/tramites')}>
                            Reportes de Trámites
                        </span>
                    </li>
                    <li className="breadcrumb-item active">RTI</li>
                </ol>
            </nav>

            <PageHeader
                variante="plano"
                icono="folder-fill"
                titulo="Reportes RTI"
                descripcion="Análisis centralizado de tiempos y estados RTI"
                accion={
                    <button
                        type="button"
                        className="btn btn-outline-primary d-flex align-items-center gap-2"
                        onClick={generarPDF}
                    >
                        <i className="bi bi-file-earmark-pdf" />
                        Exportar PDF
                    </button>
                }
            />

            {/* KPIs Cards desde el objeto resumen */}
            <div className="ds-kpis">
                <TarjetaKpi label="Total trámites RTI" valor={resumen.totalTramites} icono="person-wheelchair" tono="exito" />
                <TarjetaKpi label="Estados activos" valor={resumen.estadosActivos} icono="diagram-3" tono="exito" />
                <TarjetaKpi label="Estado predominante" valor={resumen.estadoMasFrecuenteNombre} sub={`${resumen.estadoMasFrecuenteCantidad} trámites`} icono="bar-chart" tono="exito" esTexto />
            </div>

            {/* Filtros */}
            {(() => {
                const campoDesde = (
                    <Form.Group>
                        <Form.Label>Fecha Desde</Form.Label>
                        <div className="d-flex gap-2">
                            <Form.Control
                                type="text" placeholder="dd/mm/aaaa"
                                value={filtroFechaInicioTexto || (filtroFechaInicio ? new Date(filtroFechaInicio + 'T00:00:00').toLocaleDateString('es-AR') : '')}
                                onChange={e => { const fmt = formatearFechaMientrasEscribe(e.target.value); setFiltroFechaInicioTexto(fmt); const iso = convertirTextoAFecha(fmt); if (iso) setFiltroFechaInicio(iso); }}
                                style={{ flex: 1 }}
                            />
                            <Form.Control type="date" value={filtroFechaInicio} onChange={e => setFiltroFechaInicio(e.target.value)} style={{ width: '45px' }} />
                        </div>
                    </Form.Group>
                );
                const campoHasta = (
                    <Form.Group>
                        <Form.Label>Fecha Hasta</Form.Label>
                        <div className="d-flex gap-2">
                            <Form.Control
                                type="text" placeholder="dd/mm/aaaa"
                                value={filtroFechaFinTexto || (filtroFechaFin ? new Date(filtroFechaFin + 'T00:00:00').toLocaleDateString('es-AR') : '')}
                                onChange={e => { const fmt = formatearFechaMientrasEscribe(e.target.value); setFiltroFechaFinTexto(fmt); const iso = convertirTextoAFecha(fmt); if (iso) setFiltroFechaFin(iso); }}
                                style={{ flex: 1 }}
                            />
                            <Form.Control type="date" value={filtroFechaFin} onChange={e => setFiltroFechaFin(e.target.value)} style={{ width: '45px' }} />
                        </div>
                    </Form.Group>
                );

                const chipsFiltros = [
                    filtroFechaInicio && { key: 'desde', label: `Desde: ${filtroFechaInicioTexto || filtroFechaInicio}`, onQuitar: () => { setFiltroFechaInicio(''); setFiltroFechaInicioTexto(''); } },
                    filtroFechaFin && { key: 'hasta', label: `Hasta: ${filtroFechaFinTexto || filtroFechaFin}`, onQuitar: () => { setFiltroFechaFin(''); setFiltroFechaFinTexto(''); } },
                ].filter(Boolean);

                return esMobile ? (
                    <div className="eventos-section" style={{ marginBottom: '2rem', marginTop: '2rem' }}>
                        <div className="eventos-header-row">
                            <h2 className="eventos-section-title"><i className="bi bi-funnel me-2" />Filtros</h2>
                        </div>
                        <BotonFiltros cantidad={chipsFiltros.length} onClick={() => setSheetFiltrosAbierto(true)} />
                        <ChipsFiltros chips={chipsFiltros} />
                        {sheetFiltrosAbierto && (
                            <BottomSheet
                                titulo="Filtros"
                                onCerrar={() => setSheetFiltrosAbierto(false)}
                                accionPrimaria={chipsFiltros.length > 0 ? { label: 'Limpiar', onClick: limpiarFiltros } : undefined}
                            >
                                <div className="mb-3">{campoDesde}</div>
                                {campoHasta}
                            </BottomSheet>
                        )}
                    </div>
                ) : (
                    <div className="eventos-section" style={{ marginBottom: '2rem', marginTop: '2rem' }}>
                        <div className="eventos-header-row">
                            <h2 className="eventos-section-title"><i className="bi bi-funnel me-2" />Filtros</h2>
                            <button type="button" className="btn-ver-calendario-home" onClick={limpiarFiltros}>Limpiar</button>
                        </div>
                        <Row>
                            <Col md={6}>{campoDesde}</Col>
                            <Col md={6}>{campoHasta}</Col>
                        </Row>
                    </div>
                );
            })()}

            {/* Gráficos */}
            {hayDatos ? (
                <Row className="g-4 mb-4">
                    <Col lg={6}>
                        <div className="eventos-section">
                            <h2 className="eventos-section-title">Distribución por estado</h2>
                            <p className="text-muted small mb-3">Cantidad de trámites RTI agrupados por su estado actual, en el período filtrado.</p>
                            <Pie ref={pieRef} data={datosPie} options={BASE_OPTIONS} />
                        </div>
                    </Col>
                    <Col lg={6}>
                        <div className="eventos-section">
                            <h2 className="eventos-section-title">Cantidad por estado</h2>
                            <p className="text-muted small mb-3">Mismo desglose que el gráfico anterior, en barras para comparar cantidades entre estados.</p>
                            <Bar ref={barRef} data={datosBar} options={OPCIONES_BARRAS} />
                        </div>
                    </Col>
                </Row>
            ) : (
                <div className="alert alert-info mb-4">
                    <i className="bi bi-info-circle me-2" />No hay datos disponibles para mostrar los gráficos
                </div>
            )}

            {hayDatosPromedios ? (
                <div className="eventos-section" style={{ marginBottom: '2rem' }}>
                    <h2 className="eventos-section-title">Tiempo promedio por estado</h2>
                    <p className="text-muted small mb-3">Días promedio que un trámite permanece en cada estado antes de avanzar al siguiente.</p>
                    <Bar ref={barTiempoRef} data={datosBarTiempo} options={OPCIONES_BARRAS_H} />
                </div>
            ) : (
                <div className="alert alert-info mb-4">
                    <i className="bi bi-info-circle me-2" />No hay datos de tiempo promedio disponibles
                </div>
            )}

            {/* Tabla Detalle simplificada */}
            <div className="eventos-section">
                <h2 className="eventos-section-title">Detalle por estado</h2>
                <p className="text-muted small mb-3">Cantidad, porcentaje sobre el total, tiempo promedio y clasificación de velocidad (Rápido/Normal/Lento) de cada estado.</p>
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="table-light">
                        <tr>
                            <th>Estado</th>
                            <th className="text-center">Cantidad</th>
                            <th className="text-center">Porcentaje</th>
                            <th className="text-center">Días Promedio</th>
                            <th className="text-center">Estado</th>
                        </tr>
                        </thead>
                        <tbody>
                        {porEstado.map((e) => {
                            const t = tiempoPromedio.find(tp => tp.nombreEstado === e.nombreEstado);
                            return (
                                <tr key={e.nombreEstado}>
                                    <td className="fw-bold">{e.nombreEstado}</td>
                                    <td className="text-center">{e.cantidad}</td>
                                    <td className="text-center">{e.porcentaje}%</td>
                                    <td className="text-center">{t ? `${t.diasPromedio} días` : '—'}</td>
                                    <td className="text-center">
                                            <span className={`badge ${t?.clasificacion === 'Rápido' ? 'bg-success' : t?.clasificacion === 'Normal' ? 'bg-info' : 'bg-danger'}`}>
                                                {t?.clasificacion || '—'}
                                            </span>
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default ReportesTramitesRTI;